import {
  Alert,
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { getArticleCode } from "../../utils/articleCode";
import OrderSummary from "./OrderSummary";

const formatAmount = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default function SelectedProductsPanel({
  lines,
  stockWarnings = [],
  fulfillmentWarnings = [],
  onUpdateQty,
  onRemove,
  lineCount,
  estimatedSubtotal,
  onSubmit,
  submitting,
  submitDisabled,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        position: { md: "sticky" },
        top: { md: 88 },
        maxHeight: { md: "calc(100vh - 120px)" },
        overflow: "auto",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        Selected products
      </Typography>
      {stockWarnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Some lines exceed available warehouse stock. You can still create the
          order; shortages will need later fulfillment.
        </Alert>
      )}
      {fulfillmentWarnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {fulfillmentWarnings.length === 1
            ? fulfillmentWarnings[0].message
            : `${fulfillmentWarnings.length} lines use inventory outside designated warehouses.`}
        </Alert>
      )}
      {lines.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Add products from the catalog.
        </Typography>
      ) : (
        <List dense disablePadding>
          {lines.map((line) => {
            const code = getArticleCode(line);
            const available = Number(line.available_quantity) || 0;
            const over = (line.quantity || 0) > available;
            return (
              <ListItem
                key={line.admin_product_id}
                secondaryAction={
                  <IconButton
                    edge="end"
                    size="small"
                    aria-label="Remove"
                    onClick={() => onRemove(line.admin_product_id)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                }
                sx={{ px: 0, alignItems: "flex-start" }}
              >
                <ListItemText
                  primary={line.product_name}
                  secondary={
                    <Box component="span" sx={{ display: "block" }}>
                      {code && (
                        <Typography
                          component="span"
                          variant="caption"
                          display="block"
                        >
                          {code}
                        </Typography>
                      )}
                      <Typography component="span" variant="caption">
                        Qty {line.quantity} × {formatAmount(line.unit_price)}{" "}
                        (Selling Price) = {formatAmount(line.unit_price * line.quantity)}
                        {line.mrp_price != null &&
                          Number(line.mrp_price) !==
                            Number(line.unit_price) && (
                            <> · MRP {formatAmount(line.mrp_price)}</>
                          )}
                      </Typography>
                      {over && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="warning.main"
                          display="block"
                        >
                          Available {available} — ordering more than stock
                        </Typography>
                      )}
                      {(line.warnings || []).map((warning) => (
                        <Typography
                          key={warning}
                          component="span"
                          variant="caption"
                          color="warning.main"
                          display="block"
                        >
                          {warning}
                        </Typography>
                      ))}
                      {line.outside_mapped_location && !line.warnings?.length ? (
                        <Chip
                          label="Outside mapped warehouse"
                          color="warning"
                          size="small"
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      ) : null}
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      )}
      <OrderSummary
        lineCount={lineCount}
        estimatedSubtotal={estimatedSubtotal}
        onSubmit={onSubmit}
        submitting={submitting}
        disabled={submitDisabled}
      />
    </Paper>
  );
}
